import { Injectable, makeStateKey, StateKey, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Observable, map, catchError, filter, combineLatest, of } from 'rxjs';

import { LanguageService } from '@fcom/ui-translate';
import { ConfigService, StateTransferService } from '@fcom/core';
import { SentryLogger } from '@fcom/core/services/sentry/sentry.logger';
import { isNotEmpty, isPresent } from '@fcom/core/utils';
import { finShare } from '@fcom/rx';

import {
  DestinationSearchItem,
  DestinationSearchItemType,
  DestinationSearchQueryParamNames,
  DestinationSearchResponseData,
  DestinationSearchResponseItem,
  DestinationSearchResult,
} from '../interfaces/destination-search.interface';
import { findDestinationsForQueryParams, flattenDestinations } from '../utils/flatten';
import { latinizeRegexLight, latinizeMapLight, latinize } from '../utils/latinize';

@Injectable()
export class DestinationSearchService {
  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private sentryLogger = inject(SentryLogger);
  private languageService = inject(LanguageService);
  private stateTransferService = inject(StateTransferService);

  readonly latinizeRegex = latinizeRegexLight;
  readonly latinizeMap = latinizeMapLight;
  readonly latinize = latinize(this.latinizeRegex, this.latinizeMap);

  destinationSearchResult$: Observable<DestinationSearchResult>;
  destinationSearchResultFi$: Observable<DestinationSearchResult>;

  static createStateKey = (lang: string): StateKey<DestinationSearchResult> =>
    makeStateKey<DestinationSearchResult>(`dsr-${lang}`);

  constructor() {
    this.destinationSearchResult$ = this.fetchDestinationSearch(this.languageService.langValue).pipe(finShare());
    this.destinationSearchResultFi$ = this.fetchDestinationSearch('fi-en').pipe(finShare());
  }

  public destinationSearch(restriction?: DestinationSearchQueryParamNames): Observable<DestinationSearchItem[]> {
    return this.restrictedDestinationSearchItems(restriction).pipe(
      filter(isPresent),
      map((destinations) => this.recursiveSort(destinations))
    );
  }

  public getDirectDestinations(countryCode: string): Observable<DestinationSearchItem[]> {
    return combineLatest(this.destinationSearchResult$, this.destinationSearchResultFi$).pipe(
      map(([localisedDestinations, fiDestinations]) => {
        const cc = countryCode.toLowerCase();
        const directDestinations = fiDestinations.items
          .map(({ items }) => items)
          .flat()
          .find((destination) => destination.countryCode === cc)
          ?.items.filter(({ directFlight }) => !!directFlight)
          .map(({ locationCode }) => locationCode);

        if (!directDestinations || directDestinations.length === 0) {
          return [];
        }

        return (
          localisedDestinations.items
            .map(({ items }) => items)
            .flat()
            .find((destination) => destination.countryCode === cc)
            ?.items.filter(({ locationCode }) => directDestinations.includes(locationCode))
            ?.sort((a, b) => this.sortByAlphabetical(a, b))
            ?.sort((a, b) => (a?.mainAirport && !b?.mainAirport ? -1 : !a?.mainAirport && b?.mainAirport ? 1 : 0)) || []
        );
      })
    );
  }

  /**
   * returns all destination location codes for a given country.
   */
  public getDestinationLocationCodesForCountry(countryCode: string): Observable<string[]> {
    const cc = countryCode?.toLowerCase();
    if (!cc) {
      return of([]);
    }

    return this.destinationSearchResult$.pipe(
      map((searchResult) => {
        const flattened = flattenDestinations(searchResult?.items ?? []);
        const seen = new Set<string>();
        const codes: string[] = [];

        for (const item of flattened) {
          if (
            item.type === DestinationSearchItemType.DESTINATION &&
            item.locationCode &&
            item.countryCode?.toLowerCase() === cc &&
            !seen.has(item.locationCode)
          ) {
            seen.add(item.locationCode);
            codes.push(item.locationCode);
          }
        }

        return codes;
      })
    );
  }

  private restrictedDestinationSearchItems(
    restriction?: DestinationSearchQueryParamNames
  ): Observable<DestinationSearchItem[]> {
    if (!restriction) {
      return this.destinationSearchItems();
    }
    return this.destinationSearchItems().pipe(
      map((destinations) => this.getRestrictedDestinationsTree(destinations, restriction))
    );
  }

  private destinationSearchItems(): Observable<DestinationSearchItem[]> {
    return this.destinationSearchResult$.pipe(map((searchResult: DestinationSearchResult) => searchResult?.items));
  }

  private fetchDestinationSearch(lang?: string): Observable<DestinationSearchResult> {
    return this.stateTransferService.wrapToStateCache(DestinationSearchService.createStateKey(lang), () => {
      return this.destinationSearchQuery(lang).pipe(
        map((result) => {
          // map DestinationSearchResult item to DSDestination[] by converting tags array to a tags Set
          return {
            info: result.info,
            items: this.mapResponseItemsToItems(result.items),
          };
        })
      );
    });
  }

  private mapResponseItemsToItems(items: DestinationSearchResponseItem[]): DestinationSearchItem[] {
    if (!items) {
      return;
    }
    return items.map((o) => {
      // @TODO: Remove delete responsiveImageData when CMS has migrated away from these
      if (o?.picture?.['responsiveImageData']) {
        delete o.picture['responsiveImageData'];
      }
      return {
        ...o,
        countryMaster: o.countryMaster || o.country,
        continentMaster: o.continentMaster || o.continent,
        id: (o.type + (o.titleMaster || o.title)).toLowerCase(),
        items: this.mapResponseItemsToItems(o.items),
        tags: new Set(o.tags),
        normalizedTitle: this.latinize(o.title),
        normalizedTitleMaster: this.latinize(o.titleMaster || o.title),
        titleMaster: o.titleMaster || o.title,
        directFlight: o.directFlight,
      } as DestinationSearchItem;
    });
  }

  private destinationSearchQuery(langValue: string): Observable<DestinationSearchResponseData> {
    const apiUrl = this.configService.cfg.destinationsSearchUrl;
    const xApiKey = this.configService.cfg.destinationsSearchApiKey;
    const httpOptions = {
      headers: new HttpHeaders({
        'x-api-key': xApiKey,
        'Content-Type': 'application/json',
      }),
    };
    const apiUrlLocalized = langValue ? `${apiUrl}?lang=${langValue}` : apiUrl;

    return this.http.get<DestinationSearchResponseData>(apiUrlLocalized, httpOptions).pipe(
      map((data: DestinationSearchResponseData) => {
        return data;
      }),
      catchError((response: unknown) => {
        this.sentryLogger.warn('Failed to fetch destinations for destinations main page', {
          error: (response as { error?: unknown })?.error,
        });
        return of({ items: [], info: null });
      })
    );
  }

  private getRestrictedDestinationsTree(
    allDestinations: DestinationSearchItem[],
    restrictions: DestinationSearchQueryParamNames
  ): DestinationSearchItem[] {
    const restrictToDestinationsItems = findDestinationsForQueryParams(allDestinations, restrictions);
    const restrictIDs = restrictToDestinationsItems.map((el) => el.id);
    const restrictedDestinationsTree = this.reduceRestrictedDestinations(allDestinations, restrictIDs);

    return isNotEmpty(restrictedDestinationsTree) ? restrictedDestinationsTree : allDestinations;
  }

  private reduceRestrictedDestinations(destinations: DestinationSearchItem[], restrictIDs: string[]) {
    return destinations.reduce((acc, item) => {
      const newItem = { ...item };
      const isMatch = restrictIDs.includes(item.id);

      if (!isMatch && item.items) {
        newItem.items = this.reduceRestrictedDestinations(item.items, restrictIDs);
      }
      if (isMatch || newItem.items?.length) {
        acc.push(newItem);
      }
      return acc;
    }, []);
  }

  private recursiveSort(destinations: DestinationSearchItem[]): DestinationSearchItem[] {
    if (!destinations) {
      return [];
    }
    return (
      [...destinations].sort(this.sortByAlphabetical).map((d) => ({
        ...d,
        ...((d.type !== DestinationSearchItemType.DESTINATION && {
          items: this.recursiveSort(d.items),
        }) as DestinationSearchItem),
      })) ?? []
    );
  }

  private sortByAlphabetical(a: DestinationSearchItem, b: DestinationSearchItem) {
    return a.title > b.title ? 1 : -1;
  }
}
